import React, { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Card, Column, GridContainer, H1, H2, Icon, Row, Typography } from '@vp/swan';
import { Maybe } from '@99designs/graph-utils/types';
import { __ } from '@99designs/i18n';
import { StepLayout, useNavigationClicked } from '@99designs/swan-wizard';
import { BriefProvider, NormalisedBriefProduct, useBriefContext } from '../../BriefContext';
import { FieldReview, isFieldShown } from '../Field';
import { getDisplayStatus, getIsFieldHidden } from '../Field/useIsFieldHidden';
import { FieldGroupData, FieldGroupVariant, isGroupEmpty, useFormGroups } from '../FieldGroup';
import { reviewGroupTitleWithOverrides } from '../FieldGroup/useGroupTitleWithOverrides';
import { ProductOptionsProvider } from '../FormFields/ClientConfiguredFields/ProductOptions';
import { Information } from '../Information';
import { LogomakerAsset, StepProductCard, WorkEntityDisplay } from '../StepProductCard';
import { SubmitMultiStepBriefButton } from '../SubmitBriefButton';
import { TermsAndConditions } from '../TermsAndConditions';
import { useIsEditMode, useViewContext } from '../ViewContext';
import {
    BriefProductFragment,
    BriefRequestFragment,
    StructuredBriefFragment,
} from '../brief.generated';
import { EditFormGroupModal } from './EditFormGroupModal';
import { EditFormGroupModalProvider } from './EditModalContext';
import type { InvitedDesigner } from '../InvitedDesigner';

function useHandlePrevious() {
    const nav = useNavigate();
    const { trackNavigationClick } = useNavigationClicked({
        label: 'Content: Bottom Bar',
    });

    const handlePrevious = useCallback(() => {
        const currentPath = window.location.pathname;
        const newPath = currentPath.replace(/\/review$/, '');
        const url = new URL(newPath, window.location.origin);
        trackNavigationClick(
            `${window.location.pathname}${window.location.search};${
                url.pathname + url.search
            };Bottom Bar;designBriefBackButton`,
            'designBriefBackButton'
        );
        nav(-1);
    }, [nav, trackNavigationClick]);

    return handlePrevious;
}

export type ReadOnlyReviewProps = {
    groups: FieldGroupData[];
    product: Maybe<NormalisedBriefProduct>;
};

// This component is required to trigger the product options loading
// as they can only load after the brief has been loaded by a seperate query
// In the future we should refactor this so that the configurator is a field resolver
// on the brief and can access it's parameters from the brief state on the back end and not require multiple queries
const ReadOnlyReviewInner = ({
    brief,
}: {
    brief: StructuredBriefFragment & {
        request: BriefRequestFragment | null;
        product: BriefProductFragment | null;
    };
}) => {
    const { onCompleted } = useBriefContext();
    useEffect(() => {
        if (!brief) {
            return;
        }
        onCompleted({ __typename: 'Query', brief });
    }, [brief, onCompleted]);
    const {
        intro,
        groups,
        isLoading: isCriteriaLoading,
    } = useFormGroups(brief, FieldGroupVariant.READONLY, true);

    const allFilteredGroups = [...(intro ? [intro] : []), ...groups];

    if (isCriteriaLoading) {
        return null;
    }

    return (
        <>
            {allFilteredGroups.map((group) =>
                isGroupEmpty(group) ? null : (
                    <ReviewCardReadOnly key={`${group.id}-card`} group={group} />
                )
            )}
        </>
    );
};

export function ReadOnlyReview({
    brief,
}: {
    brief: StructuredBriefFragment & {
        request: BriefRequestFragment | null;
        product: BriefProductFragment | null;
    };
}) {
    return (
        <BriefProvider>
            <ProductOptionsProvider>
                <ReadOnlyReviewInner brief={brief} />
            </ProductOptionsProvider>
        </BriefProvider>
    );
}

export function Review({
    groups,
    product,
    startingAsset,
    reportedIssues,
    groupFormSections,
    briefId,
    groupFormKey,
    setGroupFormKey,
    invitedDesigner,
}: ReadOnlyReviewProps & {
    startingAsset?: WorkEntityDisplay | LogomakerAsset;
    reportedIssues?: readonly string[] | null;
    groupFormSections: Record<string, React.ReactNode>;
    briefId: string;
    groupFormKey: number;
    setGroupFormKey: React.Dispatch<React.SetStateAction<number>>;
    invitedDesigner?: InvitedDesigner | null;
}) {
    const isEditMode = useIsEditMode();
    const {
        config: {
            showDta,
            submitWithCart,
            submitButtonCopy,
            submitButtonEditCopy,
            tenant: { reviewTitle, reviewSubtitle },
        },
    } = useBriefContext();

    const handlePrevious = useHandlePrevious();

    // Since the card is at the bottom, we're not too worried about it appearing suddenly, so we
    // won't use "isLoading" here.

    return (
        <StepLayout
            key={'review'}
            NextAction={() => (
                <SubmitMultiStepBriefButton
                    label={submitButtonCopy}
                    editLabel={submitButtonEditCopy}
                    isWithCart={submitWithCart}
                    isEditMode={isEditMode}
                    skin="primary"
                    width="standard"
                />
            )}
            PreviousAction={() =>
                isEditMode ? (
                    <span />
                ) : (
                    <Button skin="tertiary" onClick={handlePrevious} iconPosition="left">
                        <Icon iconType="arrowLeft" />
                        {__('Back')}
                    </Button>
                )
            }
            Information={() => <Information product={product} />}
            showProgressBar={false}
            maxWidth={'926px'}
        >
            <Box mb={10} mx={5} mt={5}>
                <H1 fontSkin="title-headline">{reviewTitle}</H1>
                <Typography mb="6">{reviewSubtitle}</Typography>
                {product && (
                    <StepProductCard
                        startingAsset={startingAsset}
                        reportedIssues={reportedIssues}
                        product={product}
                        invitedDesigner={invitedDesigner}
                    />
                )}
                {groups.map((group) => (
                    <ReviewCard
                        key={`${group.id}-card`}
                        group={group}
                        briefId={briefId}
                        groupFormSections={groupFormSections}
                        isEditMode={isEditMode}
                        groupFormKey={groupFormKey}
                        setGroupFormKey={setGroupFormKey}
                    />
                ))}
                {showDta && <TermsAndConditions />}
            </Box>
        </StepLayout>
    );
}

interface ReviewCardProps {
    group: FieldGroupData;
    briefId: string;
    groupFormSections: Record<string, React.ReactNode>;
    isEditMode: boolean;
    groupFormKey: number;
    setGroupFormKey: React.Dispatch<React.SetStateAction<number>>;
}

function ReviewCard({
    group,
    briefId,
    groupFormSections,
    isEditMode,
    setGroupFormKey,
}: ReviewCardProps) {
    const {
        formState: { errors },
    } = useFormContext();
    const viewContext = useViewContext();

    if (group.fields.every((f) => getIsFieldHidden(f, true))) {
        return null;
    }

    const groupErrors = group.fields.reduce<Record<string, unknown>>((acc, f) => {
        if (errors[f.id] != null) {
            acc[f.id] = errors[f.id];
        }
        return acc;
    }, {});
    const groupHasErrors = Object.keys(groupErrors).length > 0;
    const shouldShowEditButton = getShouldDisplayEditButton(group, isEditMode);
    const shouldShowPlusButton = group.fields.every((f) => !isFieldShown(f, viewContext));
    const editButtonIcon = shouldShowPlusButton ? 'plus' : 'edit';

    return (
        <EditFormGroupModalProvider
            group={group}
            setGroupFormKey={setGroupFormKey}
            briefId={briefId}
        >
            <Card
                my="6"
                p="6"
                bordered
                style={{ position: 'relative' }}
                data-testid={`review-card-${group.id}`}
            >
                <GridContainer>
                    <Row>
                        <ReviewCardContent group={group} groupErrors={groupErrors} />
                        <EditFormGroupModal
                            group={group}
                            groupHasErrors={groupHasErrors}
                            showEditButton={shouldShowEditButton}
                            editButtonIcon={editButtonIcon}
                        >
                            {groupFormSections[group.id]}
                        </EditFormGroupModal>
                    </Row>
                </GridContainer>
            </Card>
        </EditFormGroupModalProvider>
    );
}

export function getShouldDisplayEditButton(group: FieldGroupData, isEditMode: boolean) {
    return !group.fields.every((f) => {
        if (!f.display) {
            return false;
        }
        const relevantDisplay = getDisplayStatus(f.display, true, isEditMode);
        return (
            relevantDisplay === 'DISPLAY_STATUS_READ_ONLY' ||
            relevantDisplay === 'DISPLAY_STATUS_HIDDEN_GROUP' ||
            relevantDisplay === 'DISPLAY_STATUS_HIDDEN'
        );
    });
}

function ReviewCardContent({
    group,
    groupErrors = {},
}: {
    group: FieldGroupData;
    groupErrors?: Record<string, unknown>;
}) {
    return (
        <Column span={12}>
            <H2 fontSkin="title-subsection">{reviewGroupTitleWithOverrides(group)}</H2>
            {group.fields.map((field) => {
                return (
                    <React.Fragment key={`field-${field.id}`}>
                        {groupErrors[field.id] != null && field.required && (
                            <Typography mt={5} textColor={'error'} fontSkin="body-standard">
                                {field.errorMessage || __('This field is required')}
                            </Typography>
                        )}
                        <FieldReview
                            key={`field-review-${field.id}`}
                            skin="standard"
                            field={field}
                            colWidthOverride={6}
                            showTitle={group.fields.length > 1}
                        />
                    </React.Fragment>
                );
            })}
        </Column>
    );
}

export function ReviewCardReadOnly({ group }: { group: FieldGroupData }) {
    return (
        <Card my="6" p="6" bordered style={{ position: 'relative' }}>
            <GridContainer>
                <Row>
                    <ReviewCardContent group={group} />
                </Row>
            </GridContainer>
        </Card>
    );
}

// Function used to get the key prefix, i.e. logo-essential-v1 -> logo-essential
export function getKeyPrefix(key: string): string {
    const keySplitArray = key.split('-');
    const lastSegment = keySplitArray[keySplitArray.length - 1];
    // checks if last segment is a version like v1
    if (/^v\d+$/.test(lastSegment)) {
        return keySplitArray.slice(0, -1).join('-');
    }
    return key;
}
